import { useAuth } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { fetchRoles } from "@/api/roles";
import type { RoleOption } from "@/interfaces/roles";

/**
 * useRoles — fetches the role catalogue (key + label) from the backend and caches
 * it for the session. Mirrors usePermissions: the frontend never hardcodes the role
 * list or its labels, so adding a role is a single backend edit.
 *
 * Usage:
 *   const { roles, labelFor } = useRoles();
 *   <RoleChip>{labelFor(user.role)}</RoleChip>
 */
export function useRoles() {
  const { isSignedIn } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["me", "roles"],
    queryFn: fetchRoles,
    enabled: !!isSignedIn,
    staleTime: Infinity,
    gcTime: Infinity,
    select: (data) => data.roles ?? [],
  });

  const roles: RoleOption[] = data ?? [];

  /** Friendly label for a role key; falls back to the raw key if unknown/not yet loaded. */
  const labelFor = useCallback(
    (role: string): string => roles.find((r) => r.key === role)?.label ?? role,
    [roles],
  );

  return {
    /** The role catalogue in hierarchy order. */
    roles,
    /** Resolve a role key to its display label. */
    labelFor,
    /** True once the catalogue has been fetched. */
    isLoaded: !isLoading && !!data,
  };
}
